/**
 * Weidner Via6 HomeSlider.
 *
 * Full-width "peek" slide gallery shown between the property overview
 * and the floor plans list on the Home page. The active slide is
 * centered at ~70% of the viewport width and the previous/next slides
 * peek in on either side at ~15% — this gives a clear primary subject
 * while hinting at the neighbouring frames so visitors know the row
 * scrolls.
 *
 * Scrolling is fully automatic with a "shift then stop" rhythm: every
 * `SHIFT_INTERVAL_MS` a `setInterval` kicks off a `requestAnimationFrame`
 * tween (duration `TRANSITION_DURATION_MS`, cubic ease-in-out) that
 * glides `scrollLeft` to the next slide and then dwells before the next
 * shift. We drive the animation ourselves rather than using
 * `scrollTo({ behavior: 'smooth' })` so a child theme can tune the feel
 * — the browser's native smooth-scroll duration is fixed by the user
 * agent. A hidden duplicate set of slides sits to the right of the
 * originals; when the tween lands on a duplicate we silently snap
 * `scrollLeft` back to the matching original — same pixels under the
 * visitor's eye, so the wrap is invisible. The slider boots with slide
 * 2 centered so visitors land on something other than the property's
 * hero photo.
 *
 * Each slide is a dedicated editable slot group bound to
 * `photo_gallery` so the editor can swap the image and alt text
 * independently per slide while still falling back to the property's
 * first four gallery photos by default.
 *
 * Tokenized via the theme's CSS variables (`--vibe-color-*`) and
 * Tailwind font-family tokens so a child theme can re-skin it
 * without touching the JSX.
 */
import { useEffect, useRef } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useSlotGroup, EditableSlotGroup } from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';

const SLIDE_COUNT = 4;
// Initial slide (zero-indexed) that should be centered on first paint.
// Slide 2 reads as a "deeper" preview than the property's hero image
// that already sits at index 0.
const INITIAL_SLIDE_INDEX = 1;
// Cadence between shifts. This is the total cycle — animated transition
// time plus the dwell on each slide once it lands in the center.
const SHIFT_INTERVAL_MS = 3000;
// Duration of the animated slide-to-slide transition. We drive the
// scroll ourselves with `requestAnimationFrame` (rather than the
// browser's native `scrollTo({ behavior: 'smooth' })`, whose duration
// is fixed by the user agent) so we can pick a longer, more cinematic
// glide. Keep this comfortably less than `SHIFT_INTERVAL_MS` so each
// slide still has dwell time once it lands centered.
const TRANSITION_DURATION_MS = 1400;

const slide1Def = {
  label: 'Slide 1',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultField: 'photo_gallery[0].image',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultField: 'photo_gallery[0].alt_text',
      defaultValue: 'Community photo',
    },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 0,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
};

const slide2Def = {
  label: 'Slide 2',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultField: 'photo_gallery[1].image',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultField: 'photo_gallery[1].alt_text',
      defaultValue: 'Community photo',
    },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 1,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
};

const slide3Def = {
  label: 'Slide 3',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultField: 'photo_gallery[2].image',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultField: 'photo_gallery[2].alt_text',
      defaultValue: 'Community photo',
    },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 2,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
};

const slide4Def = {
  label: 'Slide 4',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultField: 'photo_gallery[3].image',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultField: 'photo_gallery[3].alt_text',
      defaultValue: 'Community photo',
    },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 3,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
};

// Tailwind-arbitrary widths used for the peek effect. Kept as
// constants so the slide-track padding and slide width stay in
// sync — center slide takes 70% of the container, leaving 15% on
// each side for the left/right peek.
const SLIDE_BASIS = 'basis-[70%]';
const TRACK_PEEK_PADDING = 'px-[15%]';

export function HomeSlider() {
  const slide1 = useSlotGroup('homeslider.slide_1', slide1Def);
  const slide2 = useSlotGroup('homeslider.slide_2', slide2Def);
  const slide3 = useSlotGroup('homeslider.slide_3', slide3Def);
  const slide4 = useSlotGroup('homeslider.slide_4', slide4Def);

  const trackRef = useRef<HTMLDivElement>(null);

  // One effect drives both initial positioning (center slide 2) and
  // the shift-and-stop loop. Each interval tick smooth-scrolls one
  // slide to the right; the visitor sees an unbroken forward conveyor
  // (…2 → 3 → 4 → 1 → 2 → 3 → 4 → 1…) and never a snap back to slide 1.
  //
  // The wrap point matters: we only snap once we've shifted *past* the
  // duplicate of slide 1 onto the duplicate of slide 2. At that moment
  // every visible cell — center slide, left peek, right peek — has a
  // pixel-identical counterpart in the original set, so swapping
  // `scrollLeft` to slide 2's original position is invisible. Snapping
  // any earlier (e.g. on the duplicate of slide 1) would lose the left
  // peek and the visitor would see slide 4's trailing strip pop out of
  // existence, which reads as a reset to the start.
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;

    // Cubic ease-in-out — slow start, slow finish, gentle glide through
    // the middle. Reads as "cinematic" rather than the browser's snappier
    // default smooth-scroll curve.
    const easeInOutCubic = (t: number) =>
      t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;

    const targetScrollFor = (index: number) => {
      const child = track.children[index] as HTMLElement | undefined;
      if (!child) return null;
      return child.offsetLeft - (track.clientWidth - child.clientWidth) / 2;
    };

    // Instant center — used for the boot frame and the invisible wrap snap.
    const jumpToIndex = (index: number) => {
      const left = targetScrollFor(index);
      if (left !== null) track.scrollLeft = left;
    };

    let rafId: number | null = null;

    // rAF-driven tween. Unlike `scrollTo({ behavior: 'smooth' })`, we
    // pick the duration and easing so a child theme could tune the feel.
    // Resolves once the final frame is painted so the caller can run the
    // wrap snap on exactly the right tick.
    const animateToIndex = (index: number, duration: number) =>
      new Promise<void>((resolve) => {
        const target = targetScrollFor(index);
        if (target === null) {
          resolve();
          return;
        }
        const start = track.scrollLeft;
        const distance = target - start;
        const startTime = performance.now();

        const step = (now: number) => {
          const t = Math.min((now - startTime) / duration, 1);
          track.scrollLeft = start + distance * easeInOutCubic(t);
          if (t < 1) {
            rafId = requestAnimationFrame(step);
          } else {
            rafId = null;
            resolve();
          }
        };
        rafId = requestAnimationFrame(step);
      });

    // Boot with slide 2 centered — no animation, just the starting frame.
    let index = INITIAL_SLIDE_INDEX;
    jumpToIndex(index);

    const intervalId = window.setInterval(() => {
      index += 1;
      void animateToIndex(index, TRANSITION_DURATION_MS).then(() => {
        // Once we've shifted onto the duplicate of slide 2 (one past the
        // duplicate of slide 1), both peeks line up with their originals
        // and we can silently snap `scrollLeft` back to the matching
        // original. The animation promise resolves on the exact frame
        // we landed, so there's no risk of snapping mid-glide.
        if (index > SLIDE_COUNT) {
          index -= SLIDE_COUNT;
          jumpToIndex(index);
        }
      });
    }, SHIFT_INTERVAL_MS);

    return () => {
      window.clearInterval(intervalId);
      if (rafId !== null) cancelAnimationFrame(rafId);
    };
  }, []);

  return (
    <section className="w-full bg-[var(--vibe-color-primary)]">
      <div
        ref={trackRef}
        className={`w-full overflow-hidden flex gap-4 md:gap-6 ${TRACK_PEEK_PADDING}`}
        style={{ scrollbarWidth: 'none' }}
      >
        <EditableSlotGroup
          groupPath="homeslider.slide_1"
          definition={slide1Def}
          className={`shrink-0 ${SLIDE_BASIS}`}
        >
          <figure className="relative w-full aspect-[3/2] overflow-hidden rounded-sm">
            <VibesitesImage
              url={slide1.image || ''}
              alt={slide1.alt || ''}
              fill
              className="absolute inset-0 h-full w-full object-cover"
            />
          </figure>
        </EditableSlotGroup>

        <EditableSlotGroup
          groupPath="homeslider.slide_2"
          definition={slide2Def}
          className={`shrink-0 ${SLIDE_BASIS}`}
        >
          <figure className="relative w-full aspect-[3/2] overflow-hidden rounded-sm">
            <VibesitesImage
              url={slide2.image || ''}
              alt={slide2.alt || ''}
              fill
              className="absolute inset-0 h-full w-full object-cover"
            />
          </figure>
        </EditableSlotGroup>

        <EditableSlotGroup
          groupPath="homeslider.slide_3"
          definition={slide3Def}
          className={`shrink-0 ${SLIDE_BASIS}`}
        >
          <figure className="relative w-full aspect-[3/2] overflow-hidden rounded-sm">
            <VibesitesImage
              url={slide3.image || ''}
              alt={slide3.alt || ''}
              fill
              className="absolute inset-0 h-full w-full object-cover"
            />
          </figure>
        </EditableSlotGroup>

        <EditableSlotGroup
          groupPath="homeslider.slide_4"
          definition={slide4Def}
          className={`shrink-0 ${SLIDE_BASIS}`}
        >
          <figure className="relative w-full aspect-[3/2] overflow-hidden rounded-sm">
            <VibesitesImage
              url={slide4.image || ''}
              alt={slide4.alt || ''}
              fill
              className="absolute inset-0 h-full w-full object-cover"
            />
          </figure>
        </EditableSlotGroup>

        {/*
         * Duplicate set for seamless looping. These are presentational
         * mirrors of the editable slides above — `aria-hidden` so a
         * screen reader doesn't announce each photo twice, and rendered
         * as plain divs (not EditableSlotGroup) so the editor only ever
         * sees four slide controls instead of eight.
         */}
        <div aria-hidden="true" className={`shrink-0 ${SLIDE_BASIS}`}>
          <figure className="relative w-full aspect-[3/2] overflow-hidden rounded-sm">
            <VibesitesImage
              url={slide1.image || ''}
              alt=""
              fill
              className="absolute inset-0 h-full w-full object-cover"
            />
          </figure>
        </div>
        <div aria-hidden="true" className={`shrink-0 ${SLIDE_BASIS}`}>
          <figure className="relative w-full aspect-[3/2] overflow-hidden rounded-sm">
            <VibesitesImage
              url={slide2.image || ''}
              alt=""
              fill
              className="absolute inset-0 h-full w-full object-cover"
            />
          </figure>
        </div>
        <div aria-hidden="true" className={`shrink-0 ${SLIDE_BASIS}`}>
          <figure className="relative w-full aspect-[3/2] overflow-hidden rounded-sm">
            <VibesitesImage
              url={slide3.image || ''}
              alt=""
              fill
              className="absolute inset-0 h-full w-full object-cover"
            />
          </figure>
        </div>
        <div aria-hidden="true" className={`shrink-0 ${SLIDE_BASIS}`}>
          <figure className="relative w-full aspect-[3/2] overflow-hidden rounded-sm">
            <VibesitesImage
              url={slide4.image || ''}
              alt=""
              fill
              className="absolute inset-0 h-full w-full object-cover"
            />
          </figure>
        </div>
      </div>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'HomeSlider',
  displayName: 'Home Slider',
  tokens: {},
  slotGroups: {
    'homeslider.slide_1': slide1Def,
    'homeslider.slide_2': slide2Def,
    'homeslider.slide_3': slide3Def,
    'homeslider.slide_4': slide4Def,
  },
};

(HomeSlider as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default HomeSlider;
